import { NavElement, NavItemGroup, isNavItemGroup } from 'Modules/Navigation/whitelabel/routes';
import styles from './NavSidebar.module.css';
import { useContext, useState } from 'react';
import cn from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from 'Modules/Auth/contexts/AuthContext';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';
import { Image } from '@round/ui-kit';
import ClientSelect from 'Modules/Plans/Clients/components/ClientSelect/ClientSelect';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { assets } from '@round/whitelabel-ui';

const { LogoShort: RoundLogoShort, ChevronRight: ArrowIcon, Logout: LogoutIcon } = assets;

type Props = {
    items: NavElement[];
};

const NavSidebar = ({ items }: Props) => {
    const authContext = useContext(AuthContext);
    const location = useLocation();
    const isCurrentPath = (path: string) => location.pathname.includes(path);

    const [isExpanded, setIsExpanded] = useState(false);
    const [leafItemsIndex, setLeafItemsIndex] = useState<number | null>(null);
    const [delay, setDelay] = useState<ReturnType<typeof setTimeout> | null>(null);

    const withDelay = (callback: () => void, timeout: number) => {
        setDelay(setTimeout(callback, timeout));
    };

    const selectedLeafItems = (
        items.find((item, index) => isNavItemGroup(item) && index === leafItemsIndex) as NavItemGroup | undefined
    )?.items;

    const shouldShowRoundLink = useCheckUserGroupsAccess(['round_product']);
    const { selectedClient } = useClientSelect();

    return (
        <nav
            className={styles.container}
            onMouseLeave={() => {
                setIsExpanded(false);
                setLeafItemsIndex(null);
            }}
        >
            {/* This dictates how much width the navbar takes up from the main page. It's hidden, and allows us
                to expand the nav container with absolute positioning without disrupting the page layout */}
            <div className={styles.spine} />

            <div className={cn(styles.navContainer, { [styles.isExpanded]: isExpanded })}>
                <div
                    className={styles.logosContainer}
                    onMouseEnter={() => {
                        withDelay(() => {
                            setLeafItemsIndex(null);
                            setIsExpanded(true);
                        }, 100);
                    }}
                    onMouseLeave={() => {
                        if (delay) {
                            clearTimeout(delay);
                        }
                    }}
                >
                    <div className={styles.logosGrid}>
                        <RoundLogoShort className={cn(styles.logo, styles.roundLogo)} />{' '}
                        {!!isExpanded && <span>Round</span>}
                        <hr />
                        <Image src={selectedClient?.logo || ''} className={cn(styles.logo, styles.sonyLogo)} />
                        {!!isExpanded && <ClientSelect />}
                    </div>
                </div>

                <ul className={styles.navList}>
                    {items.map((item, index) => {
                        const Icon = item.icon;

                        if (isNavItemGroup(item)) {
                            return (
                                <li
                                    className={cn(styles.navItem, {
                                        [styles.active]:
                                            item.items.some((item) => isCurrentPath(item.url)) ||
                                            index === leafItemsIndex,
                                    })}
                                    key={item.title}
                                    onMouseEnter={() => {
                                        withDelay(() => {
                                            setIsExpanded(true);
                                            setLeafItemsIndex(index);
                                        }, 100);
                                    }}
                                    onMouseLeave={() => {
                                        if (delay) {
                                            clearTimeout(delay);
                                        }
                                    }}
                                >
                                    <Icon className={styles.navIcon} />

                                    {isExpanded && (
                                        <>
                                            <span className={styles.navTitle}>{item.title}</span>
                                            <ArrowIcon className={styles.arrowIcon} />
                                        </>
                                    )}
                                </li>
                            );
                        }

                        return (
                            <Link to={item.url} key={item.url}>
                                <li
                                    className={cn(styles.navItem, {
                                        [styles.active]: isCurrentPath(item.url),
                                    })}
                                    onMouseEnter={() => {
                                        withDelay(() => {
                                            setLeafItemsIndex(null);
                                            setIsExpanded(true);
                                        }, 100);
                                    }}
                                    onMouseLeave={() => {
                                        if (delay) {
                                            clearTimeout(delay);
                                        }
                                    }}
                                >
                                    <Icon className={styles.navIcon} />
                                    {isExpanded && <span className={styles.navTitle}>{item.title}</span>}
                                </li>
                            </Link>
                        );
                    })}
                </ul>

                <ul className={styles.staticList}>
                    {shouldShowRoundLink && (
                        <Link to={'/campaigns/'}>
                            <li
                                className={cn(styles.navItem, styles.logout)}
                                onMouseEnter={() => {
                                    withDelay(() => {
                                        setLeafItemsIndex(null);
                                        setIsExpanded(true);
                                    }, 100);
                                }}
                                onMouseLeave={() => {
                                    if (delay) {
                                        clearTimeout(delay);
                                    }
                                }}
                            >
                                <RoundLogoShort className={styles.navIcon} />
                                {isExpanded && <span className={styles.navTitle}>Round Dashboard</span>}
                            </li>
                        </Link>
                    )}

                    <li
                        className={cn(styles.navItem, styles.logout)}
                        onClick={() => authContext.logout?.()}
                        onMouseEnter={() => {
                            withDelay(() => {
                                setLeafItemsIndex(null);
                                setIsExpanded(true);
                            }, 100);
                        }}
                        onMouseLeave={() => {
                            if (delay) {
                                clearTimeout(delay);
                            }
                        }}
                    >
                        <LogoutIcon className={styles.navIcon} />
                        {isExpanded && <span className={styles.navTitle}>Logout</span>}
                    </li>
                </ul>
            </div>

            {!!selectedLeafItems?.length && (
                <div className={styles.leaf}>
                    <ul className={styles.navList}>
                        {selectedLeafItems.map((item) => {
                            return (
                                <Link to={item.url} key={item.url}>
                                    <li
                                        className={cn(styles.navItem, {
                                            [styles.active]: isCurrentPath(item.url),
                                        })}
                                        key={item.url}
                                    >
                                        <span>{item.title}</span>
                                    </li>
                                </Link>
                            );
                        })}
                    </ul>
                </div>
            )}
        </nav>
    );
};

export default NavSidebar;
